import type {GuidedItemsV2, GuidedV2SectionSource} from '../models/models';
import {h, type JSX} from '@stencil/core';
import {GuidedV2ResultListSection} from './result-list-section/result-list';
import type {labelProviderInterface} from '../../LabelProviderInterface';
import {
  mapGuidedV2ItemsToGuidedV2ResultList,
  mapGuidedV2ItemToGuidedV2KeywordSuggestions,
  mapGuidedV2ItemToHistoryItem
} from './mappers';
import {GuidedFooterBanner} from './footer-banner/guided-banner';
import {SuggestionsBar} from './suggestions/guided-suggestions';
import {onlyMatchingSource} from '../utils';
import {GuidedSkeleton} from './skeleton/skeleton';
import {
  type GuidedQuickLinkImportance,
  GuidedUiDivider, GuidedUiGenaiBanner, GuidedUiNoLinksSection, GuidedUiNoResultsSection, GuidedUiPnaSection,
  type GuidedV2SubcomponentUiProvider
} from '../models/ui';
import {
  GuidedGenericQuickLink,
  GuidedHistoryLinkDetails,
  GuidedQuickLinkDetails,
  type GuidedV2Link
} from '../models/quick-link';
import type {NonEmptyArray} from '../models/models';
import {
  AbstractGuidedUiElement,
  GuidedUiFooterBanner,
  GuidedUiHistoryOfSearches,
  GuidedUiKeywordSuggestions,
  GuidedUiPopularQueries,
  GuidedUiSkeleton
} from '../models/ui';
import {GuidedV2HistoryOfSearches as HistoryOfSearches} from './history/history';
import type {GuidedV2Emitters} from '../models/eventNames';
import { NoLinksMessage } from './no-links-message/no-links-message';
import { NoResultMessage } from './no-results-message/no-results-message';
import { GuidedBannerGenai } from './genai-banner/guided-banner-genai';

export class GuidedUiQuickLinkSection extends AbstractGuidedUiElement{
  type: 'quick-links';
  clickTarget: string;
  slots: number;
  name: string;
  importance: GuidedQuickLinkImportance = 'default';
  itemSources: NonEmptyArray<GuidedV2SectionSource>;
}

export type GuidedV2FactoryState<T extends GuidedQuickLinkDetails=GuidedQuickLinkDetails > ={
    eventEmitters: GuidedV2Emitters,
    labelProvider: labelProviderInterface,
    searchTerm: string,
    highlightedIndex: number,
    results: GuidedItemsV2<T>,
    strategyLinkRenderer: GuidedV2SubcomponentUiProvider,
    badgeHighlightThreshold: number,
    markupHighlightThreshold: number,
    setSearchTermCallback: (term: string) => void,
    isMobile?: boolean,
    unifiedCheckout?: boolean,
};

const GuidedV2Skeleton = (inputData: GuidedUiSkeleton)=>{
    return <GuidedSkeleton
      itemWidth={inputData.itemWidth}
      itemHeight={inputData.itemHeight}
      resultSlots={inputData.resultSlots}
      displayMode={inputData.displayMode}
      label={inputData.label}
    ></GuidedSkeleton>;
};

const GuidedV2Divider = ( _state: GuidedV2FactoryState,
) => {
    return <qds-divider></qds-divider>;
};

const GuidedV2FooterBanner = (
  inputData:GuidedUiFooterBanner,
  state: Omit<GuidedV2FactoryState,'results'>,
)=>{
    const {
      text,
      description,
      href,
      buttonText,
      src,
      target,
      name
    } = inputData; //comes from configuration

    const {
      eventEmitters,
    } = state;
    const bannerText = state.labelProvider(`guided-v2.banner.text.${name}`,text);
    const bannerDescription = state.labelProvider(`guided-v2.banner.description.${name}`,description);
    const bannerButtonText = state.labelProvider(`guided-v2.banner.buttonText.${name}`,buttonText);

    return <GuidedFooterBanner
      text={bannerText}
      href={href}
      description={bannerDescription}
      buttonText={bannerButtonText}
      src={src}
      eventEmitters={eventEmitters}
      target={target}
    ></GuidedFooterBanner>;
};

const GuidedV2GenaiBanner = (
  inputData:GuidedUiGenaiBanner,
  state: Omit<GuidedV2FactoryState,'results'>,
)=>{
    const {
      text,
      description,
      href,
      buttonText,
      target,
      brand,
    } = inputData; //comes from configuration

    const {
      eventEmitters,
      searchTerm
    } = state;

    const bannerText = state.labelProvider(`guided-v2.banner.text.${text}`,text);
    const bannerDescription = state.labelProvider(`guided-v2.banner.description.${description}`,description);
    const bannerButtonText = state.labelProvider(`guided-v2.banner.buttonText.${buttonText}`,buttonText);

    return <GuidedBannerGenai
      text={bannerText}
      description={bannerDescription}
      buttonText={bannerButtonText}
      eventEmitters={eventEmitters}
      href={href}
      target={target}
      searchTerm={searchTerm}
      brand={brand}
    ></GuidedBannerGenai>;
};

const GuidedV2ResultListFactory = <T extends GuidedQuickLinkDetails> (
  inputData:GuidedUiQuickLinkSection,
  state: GuidedV2FactoryState<T>,
)=>{
    const {
      itemSources,
      importance,
      slots,
    } = inputData; //comes from configuration



    const {
      labelProvider,
      results, //the API response
      searchTerm,
      highlightedIndex,
      eventEmitters,
      strategyLinkRenderer,
      badgeHighlightThreshold,
      markupHighlightThreshold,
      unifiedCheckout,
    } = state;

    const name = labelProvider(`guided-v2.quicklink-section.name.${inputData?.name?.toLowerCase()}`,inputData.name);

    const links: GuidedV2Link<GuidedQuickLinkDetails>[] = mapGuidedV2ItemsToGuidedV2ResultList(
      results,
      itemSources,
      labelProvider,
      slots,
      searchTerm,
      badgeHighlightThreshold,
      markupHighlightThreshold,
      unifiedCheckout,
    );


    const clickTarget = inputData.clickTarget
      ?.replace('$TERM',encodeURIComponent(searchTerm))
    ;

    //TODO: limit items to number of slots
    //TODO: insert client side highlighting for badges
    //TODO: insert UI keyboard nav highlighting

    return <GuidedV2ResultListSection
      resultGroupName={name}
      resultGroupOpenUrl={clickTarget}
      links={links}
      strategyLinkRenderer={strategyLinkRenderer}
      highlightedIndex={highlightedIndex}
      eventEmitters={eventEmitters}
      labelProvider={labelProvider}
      unifiedCheckout={unifiedCheckout}
    ></GuidedV2ResultListSection>;
};

const GuidedV2NoLinksMessageFactory = <T extends GuidedQuickLinkDetails>(
  inputData:GuidedUiNoLinksSection,
  state: GuidedV2FactoryState<T>,
) => {
    const {
      itemSources,
      message,
      messageName,
      clickTarget,
    } = inputData; //comes from configuration

    const {
      results,
      searchTerm,
      // highlightedIndex,
      labelProvider,
      eventEmitters,
    } = state;

    const foundResults = results.filter(onlyMatchingSource(itemSources));

    if(foundResults.length > 0)
      return <div data-message="results found, not showing message"></div>;

    return <NoLinksMessage
      message={labelProvider(`guided-v2.no-links-message.${messageName}`,message)}
      searchTerm={searchTerm}
      clickTarget={clickTarget}
      eventEmitters={eventEmitters}
      ctaMessage={labelProvider('guided-v2-no-links-message.cta-message','View All Results')}
    ></NoLinksMessage>;
};


const GuidedV2NoResultsMessageFactory = <T extends GuidedQuickLinkDetails>(
  inputData:GuidedUiNoResultsSection,
  state: GuidedV2FactoryState<T>,
) => {
    const {
      itemSources,
      message,
      messageName,
      clickTarget,
      subTitle
    } = inputData; //comes from configuration

    const {
      results,
      searchTerm,
      labelProvider,
      eventEmitters,
    } = state;

    const foundResults = results.filter(onlyMatchingSource(itemSources));

    if(foundResults.length > 0)
      return <div data-message="results found, not showing message"></div>;

    return <NoResultMessage
      message={labelProvider(`guided-v2.no-links-message.${messageName}`,message)}
      searchTerm={searchTerm}
      eventEmitters={eventEmitters}
      subTitle={subTitle}
    ></NoResultMessage>;
};


/**
 * Shows the keyword suggestions in the guided search. Keywords expanding
 * current search query. The same searcher as in Popular Queries but with
 * search term for the query
 * @param inputData {GuidedV2KeywordSuggestions}
 * @param state {GuidedV2FactoryState}
 */
const GuidedV2KeywordSuggestions = <T extends GuidedQuickLinkDetails> (
  inputData:GuidedUiKeywordSuggestions,
  state: GuidedV2FactoryState<T>,
)=>{
    const {
      itemSources,
      label,
      maxItems,
    } = inputData; //comes from configuration

    const {
      labelProvider,
      results, //the API response
      // searchTerm,
      eventEmitters,
      isMobile,
    } = state;

    // console.log('Keyword suggestion results',results);

    const matchingResults = results
      .filter(onlyMatchingSource(itemSources))
      .slice(0,maxItems)
      .map(mapGuidedV2ItemToGuidedV2KeywordSuggestions)
    ;
    // console.log('Keyword suggestion matching items',matchingResults);

    if(matchingResults.length == 0)
      return <span data-err="Keyword suggestions list is empty"></span>;

    //main label: `guided-v2.keyword-suggestions.label.${label}`
    //secondary label:  'guided-v2.keyword-suggestions.label'
    //falback: label
    const suggestionsLabel = labelProvider(
      `guided-v2.keyword-suggestions.label.${label}`,
      labelProvider( //here is double-item for the label if there are multiple `guided-v2.keyword-suggestions.label`
        // items. So they should be distinct. That's why the first one is more specific
        'guided-v2.keyword-suggestions.label',
        label
      )
    );

    return <div class="guided-v2-keyword-suggestions add-selector-strength"><SuggestionsBar
      results={matchingResults}
      label={suggestionsLabel}
      eventEmitters={eventEmitters}
      isMobile={isMobile}
    /></div>;
};

//
/**
 * Shows the popular queries in the guided search. Keywords shown before user
 * has typed anything in the search box. The same searcher as in the Seyword Suggestions
 * but with "*" as query
 * @param inputData
 * @param state
 */
const GuidedV2PopularQueries = <T extends GuidedQuickLinkDetails> (
  inputData:GuidedUiPopularQueries,
  state: GuidedV2FactoryState<T>,
)=>{
    const {
      itemSources,
      label,
      maxItems,
    } = inputData; //comes from configuration

    const {
      labelProvider,
      eventEmitters,
      results, //the API response
    } = state;

    const matchingResults = results
      .filter(onlyMatchingSource(itemSources))
      .slice(0,maxItems)
      .map(mapGuidedV2ItemToGuidedV2KeywordSuggestions)
    ;

    if(matchingResults.length == 0)
      return <span data-err="Popular queries list is empty"></span>;

    const suggestionsLabel = labelProvider(
      `GuidedV2.popular-queries.label.${label}`, //TODO: labelProvider kluczyki
      label
    );

    return <div class="guided-v2-popular-queries"><SuggestionsBar
      results={matchingResults}
      label={suggestionsLabel}
      eventEmitters={eventEmitters}
    /></div>;
};

const GuidedV2HistoryOfSearches = (
  inputData: GuidedUiHistoryOfSearches,
  state: GuidedV2FactoryState,
) => {

    const {
      resultSlots,
      itemSources,
    } = inputData;

    const {
      results,
      searchTerm,
      highlightedIndex,
      labelProvider,
      setSearchTermCallback,
      eventEmitters,

    } = state;

    const historyLabel = labelProvider(
      'guided-v2.history-of-searches.label',
      'Recent searches'
    );


    const historyItems = results
      .filter(onlyMatchingSource(itemSources))
      .slice(0,resultSlots)
      .map(item=>mapGuidedV2ItemToHistoryItem(
        item as GuidedV2Link<GuidedGenericQuickLink>,
        setSearchTermCallback,
        searchTerm,
      ))
    ;

    return <HistoryOfSearches
      highlightedIndex={highlightedIndex}
      historyLabel={historyLabel}
      results={historyItems}
      eventEmitters={eventEmitters}
    />;
};


/**
 * Map of UI classes to be used in the guided search, each must inherit from AbstractGuidedUiElement
 * To extract the actual Component, use `GuidedUiElementFactory` function
 */
const UiClassMap = {
  GuidedUiFooterBanner,
  GuidedUiKeywordSuggestions,
  GuidedUiPopularQueries,
  GuidedUiQuickLinkSection,
  GuidedUiNoLinksSection,
  GuidedUiHistoryOfSearches,
  GuidedUiSkeleton,
} as const;
// Define a type to represent the acceptable class names
export type GuidedV2UiComponents = keyof typeof UiClassMap;

export function GuidedUiElementFactory<LinkType extends GuidedQuickLinkDetails = GuidedQuickLinkDetails>(
  data: AbstractGuidedUiElement,
  state: GuidedV2FactoryState<LinkType>,
): JSX.Element{
    const inputData = (()=>{
        switch(data.type){
        case 'availability-suggestions': return data as GuidedUiPnaSection;
        case 'quick-links': return data as GuidedUiQuickLinkSection;
        case 'no-links-message': return data as GuidedUiNoLinksSection;
        case 'popular-queries': return data as GuidedUiPopularQueries;
        case 'keyword-suggestions': return data as GuidedUiKeywordSuggestions;
        case 'footer-banner': return data as GuidedUiFooterBanner;
        case 'genai-banner': return data as GuidedUiGenaiBanner;
        case 'history-of-searches': return data as GuidedUiHistoryOfSearches;
        case 'skeleton': return data as GuidedUiSkeleton;
        case 'divider': return data as GuidedUiDivider;
        case 'no-result-message': return data as GuidedUiNoResultsSection;
        default: return null;
        }
    })();

    if(inputData === null){
      return <div data-err="Invalid component type" data-type={data.type}></div>;
    }
    switch(inputData.type){
    case 'quick-links': return GuidedV2ResultListFactory(inputData,state);
    case 'no-links-message': return GuidedV2NoLinksMessageFactory(inputData,state);
    case 'no-result-message': return GuidedV2NoResultsMessageFactory(inputData,state);
    case 'popular-queries': return GuidedV2PopularQueries(inputData,state);
    case 'keyword-suggestions': return GuidedV2KeywordSuggestions(inputData,state);
    case 'footer-banner': return GuidedV2FooterBanner(inputData,state);
    case 'genai-banner': return GuidedV2GenaiBanner(inputData,state);
    case 'history-of-searches': return GuidedV2HistoryOfSearches(inputData, state);
    case 'skeleton': return GuidedV2Skeleton(inputData);
    case 'divider': return GuidedV2Divider(state);

    default: return <div data-err="No such component" data-type={inputData}></div>;
    }
}
